<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, watch, inject } from "vue"
import type { Address } from "@types"
import type { Services } from "@services"
import type { AddressSearchResult } from "@core/services/address.service"
import type { AutoCompleteCompleteEvent } from "primevue/autocomplete"

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /**
     * Valeur de l'adresse (v-model)
     */
    modelValue?: Address | null
    /**
     * Identifiant du champ
     */
    inputId?: string
    /**
     * Label du champ
     */
    label?: string
    /**
     * Message d'aide
     */
    hint?: string
    /**
     * Message d'erreur
     */
    error?: string
    /**
     * Champ obligatoire
     */
    required?: boolean
    /**
     * Champ désactivé
     */
    disabled?: boolean
    /**
     * Champ en lecture seule
     */
    readonly?: boolean
}

interface Emits {
    /**
     * Émis lors du changement de valeur
     */
    (e: "update:modelValue", value: Address | null): void
    /**
     * Émis lors du changement de coordonnées
     */
    (e: "update:coordinates", value: [number, number] | null): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    inputId: "input-address",
    label: "",
    hint: "",
    error: "",
    required: false,
    disabled: false,
    readonly: false,
})

const emit = defineEmits<Emits>()

// ================================
// SERVICES
// ================================
const services = inject<Services>("services")

// ================================
// ÉTAT
// ================================

/**
 * Valeur sélectionnée dans l'autocomplete
 */
const selectedValue = ref<AddressSearchResult | string | null>(null)

/**
 * Liste des suggestions
 */
const suggestions = ref<AddressSearchResult[]>([])

/**
 * Indique si la recherche est en cours
 */
const isLoading = ref(false)

/**
 * AbortController pour annuler les requêtes
 */
const controller = ref<AbortController | null>(null)

// ================================
// WATCHERS
// ================================

/**
 * Synchronise la valeur sélectionnée avec le modelValue
 */
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue) {
            // Construire un label à partir des champs disponibles
            const label =
                newValue.inline_name ||
                [newValue.street, newValue.zipcode, newValue.city]
                    .filter(Boolean)
                    .join(", ") ||
                ""

            // Si on a une adresse valide, on crée un objet compatible
            if (label || newValue.street || newValue.city || newValue.zipcode) {
                selectedValue.value = {
                    label,
                    name: newValue.street || "",
                    city: newValue.city || "",
                    postcode: newValue.zipcode || "",
                    citycode: newValue.citycode,
                    street: newValue.street,
                } as AddressSearchResult
            } else {
                selectedValue.value = null
            }
        } else {
            selectedValue.value = null
        }
    },
    { immediate: true }
)

// ================================
// LOGIQUE MÉTIER
// ================================

/**
 * Recherche des adresses via l'API
 * @param event - Événement de complétion automatique
 */
async function onSearch(event: AutoCompleteCompleteEvent) {
    const query = event.query

    if (!query || query.length < 3) {
        suggestions.value = []
        return
    }

    // Annuler la requête précédente
    controller.value?.abort()
    controller.value = new AbortController()

    isLoading.value = true

    try {
        if (!services?.$searchAddress) {
            console.warn("Service $searchAddress non disponible")
            return
        }

        const results = await services.$searchAddress({
            search: query,
            limit: 10,
            controller: controller.value,
        })

        suggestions.value = results
    } catch (error: any) {
        if (error.name !== "AbortError") {
            console.error("Erreur lors de la recherche d'adresse:", error)
        }
        suggestions.value = []
    } finally {
        isLoading.value = false
    }
}

// ================================
// INTERACTIONS
// ================================

/**
 * Sélectionne une adresse dans les suggestions
 * @param result - Résultat de recherche sélectionné
 */
function onSelectAddress(result: AddressSearchResult) {
    const address: Address = {
        street: result.housenumber
            ? `${result.housenumber} ${result.street || result.name}`
            : result.street || result.name || "",
        zipcode: result.postcode || "",
        city: result.city || "",
        citycode: result.citycode,
        country: "France",
        inline_name: result.label,
    }

    emit("update:modelValue", address)

    if (result.coordinates) {
        emit("update:coordinates", result.coordinates)
    }
}

/**
 * Efface la valeur
 */
function onClear() {
    selectedValue.value = null
    suggestions.value = []
    emit("update:modelValue", null)
    emit("update:coordinates", null)
}
</script>

<template>
    <BaseInputWrapper
        :input-id="inputId"
        :label="label"
        :hint="hint"
        :error="error"
        :required="required"
    >
        <!-- ================================ -->
        <!-- AUTOCOMPLETE PRIMEVUE -->
        <!-- ================================ -->
        <BaseAutoComplete
            v-model="selectedValue"
            :input-id="inputId"
            :suggestions="suggestions"
            :disabled="disabled"
            :loading="isLoading"
            option-label="label"
            :min-length="3"
            :delay="500"
            fluid
            :invalid="!!error"
            :pt="{
                pcInputText: {
                    root: {
                        readonly: readonly,
                    },
                },
            }"
            @complete="onSearch"
            @option-select="onSelectAddress($event.value)"
            @clear="onClear"
        >
            <!-- ================================ -->
            <!-- TEMPLATE DES OPTIONS -->
            <!-- ================================ -->
            <template #option="{ option }">
                <div class="flex flex-col gap-0.5">
                    <span class="text-sm font-medium">{{ option.label }}</span>
                    <span v-if="option.context" class="text-xs text-gray-500">
                        {{ option.context }}
                    </span>
                </div>
            </template>

            <!-- ================================ -->
            <!-- TEMPLATE VIDE -->
            <!-- ================================ -->
            <template #empty>
                <div class="px-3 py-2 text-sm text-gray-500">
                    Aucune adresse trouvée
                </div>
            </template>
        </BaseAutoComplete>
    </BaseInputWrapper>
</template>
